import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useProgressWithAutoRefresh } from "../../../shared/contexts/ProgressContext";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";
import { normalizeAttachments } from "../../../shared/utils/attachmentUtils";
import { isMockEnabled, getMockRequestDetail } from "../../../shared/dev/mockData";
import { AuthApiService } from "../../auth/api/AuthApiService";
import QRDoorSignPrint from "../components/QRDoorSignPrint";

const RequestDetailScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const { t, isRTL, language } = useLanguage();
  const { doctorInfo, progress } = useProgressWithAutoRefresh();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId } = useParams<{ requestId: string }>();
  // Get request type from navigation state or progress API
  const locationState = location.state as { requestType?: string } | null;
  const getRequestType = (): string => {
    // First try navigation state
    if (locationState?.requestType) {
      return locationState.requestType;
    }
    // Fallback to progress API
    const activeRequest = progress?.active_requests?.find(
      (r: any) => r.request_id === requestId
    );
    return activeRequest?.request_type || 'registration';
  };

  useEffect(() => {
    loadRequestDetail();
  }, [requestId, progress]);

  // Staff requested corrections while the doctor was on this page: route to
  // /corrections as soon as refreshed progress shows it (mirrors
  // useStepRedirect's decision — previously only a re-login surfaced it).
  useEffect(() => {
    const needsCorrections = progress?.active_requests?.find(
      (r: any) => r.status === 'changes_requested' && (r.corrections?.corrections?.length ?? 0) > 0
    );
    if (needsCorrections) {
      navigate('/corrections', { replace: true });
    }
  }, [progress, navigate]);

  // Hide/show logout and language buttons when lightbox is open
  useEffect(() => {
    if (selectedImage) {
      // Hide buttons by adding a class to body
      document.body.classList.add('lightbox-open');
    } else {
      document.body.classList.remove('lightbox-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('lightbox-open');
    };
  }, [selectedImage]);

  const loadRequestDetail = async () => {
    if (!requestId) {
      navigate("/requests", { replace: true });
      return;
    }

    setLoading(true);
    setError(null);

    // Mock mode - return mock data
    if (isMockEnabled()) {
      setRequest(getMockRequestDetail());
      setLoading(false);
      return;
    }

    try {
      const authApi = AuthApiService.getInstance();
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const requestType = getRequestType();

      // Use appropriate endpoint based on request type
      let endpoint: string;
      if (requestType === 'onboarding') {
        endpoint = `${baseUrl}/clinic/doctor/onboarding/${requestId}/with-attachments`;
      } else if (requestType === 'closure') {
        endpoint = `${baseUrl}/clinic/doctor/closure/${requestId}/with-attachments`;
      } else {
        // Default to registration endpoint
        endpoint = `${baseUrl}/clinic/doctor/registration/${requestId}/with-attachments`;
      }

      console.log(`[RequestDetailScreen] Fetching ${requestType} request from: ${endpoint}`);

      const response = await authApi.fetchWithAuth(endpoint);

      if (!response.ok) {
        if (response.status === 404) {
          setError(t("request_not_found"));
        } else {
          setError(t("failed_to_load_request_details"));
        }
        setLoading(false);
        return;
      }

      const result = await response.json();
      const requestData = result.data || result;
      // Normalize attachments — API may return as "documents" or "attachments"
      const rawDocs = requestData.attachments || requestData.documents;
      if (rawDocs) {
        requestData.documents = normalizeAttachments(rawDocs);
      }
      setRequest(requestData);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("failed_to_load_request_details"));
      setLoading(false);
    }
  };

  const handlePrintQRDoorSign = () => {
    window.print();
  };

  // The QR is keyed by the approved clinic rather than by this request, so it
  // keeps working once the request is archived, and registration and
  // existing-clinic onboarding share one code path. Only an approved request
  // has an approved clinic to point at.
  const clinicId = progress?.clinic_id;
  const canPrintQR = request?.status === "approved" && !!clinicId;

  if (loading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  if (error || !request) {
    return (
      <ResponsiveFormLayout
        showLanguageToggle={true}
        icon="⚠️"
        iconSize="w-24 h-24 text-6xl"
        title={t("error")}
        subtitle={error || t("request_not_found")}
        maxHeight="90vh"
      >
        <button
          onClick={() => navigate("/requests")}
          className="w-full min-h-[56px] px-6 py-4 rounded-2xl bg-[#2E5D7D] text-white text-base font-semibold"
        >
          {t("back_to_requests")}
        </button>
      </ResponsiveFormLayout>
    );
  }

  const InfoRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => {
    if (!value) return null;
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} className="flex justify-between items-start py-2 border-b border-gray-100">
        <span className="text-sm text-gray-600 font-medium">{label}:</span>
        <span className="text-sm text-gray-900 font-semibold">{value}</span>
      </div>
    );
  };

  const getRequestTypeLabel = (type: string) => {
    if (type === 'registration') return t("clinic_registration");
    if (type === 'onboarding') return t("clinic_onboarding");
    return type;
  };

  const getStatusColor = (status: string) => {
    if (status === 'pending') return 'bg-yellow-50 border-yellow-200';
    if (status === 'draft') return 'bg-blue-50 border-blue-200';
    if (status === 'preliminary_approved') return 'bg-teal-50 border-teal-200';
    if (status === 'approved') return 'bg-green-50 border-green-200';
    if (status === 'rejected') return 'bg-red-50 border-red-200';
    return 'bg-gray-50 border-gray-200';
  };

  const getStatusIcon = (status: string) => {
    if (status === 'pending') return '⏳';
    if (status === 'draft') return '📝';
    if (status === 'preliminary_approved') return '☑️';
    if (status === 'approved') return '✅';
    if (status === 'rejected') return '❌';
    return '📄';
  };

  // Helper function to translate data values
  const translateValue = (value: string | number | null | undefined): string => {
    if (!value) return '';

    const stringValue = String(value);

    // Try to translate common data values
    // Governorates - capitalize first letter for translation key
    const capitalizedValue = stringValue.charAt(0).toUpperCase() + stringValue.slice(1).toLowerCase();

    // Try capitalized version first (for governorates)
    if (t(capitalizedValue) !== capitalizedValue) {
      return t(capitalizedValue);
    }

    // Try lowercase version (for morning/evening)
    if (t(stringValue.toLowerCase()) !== stringValue.toLowerCase()) {
      return t(stringValue.toLowerCase());
    }

    // Return original value if no translation found
    return stringValue;
  };

  return (
    <>
      <ResponsiveFormLayout
        showLanguageToggle={true}
        icon="📋"
        iconSize="w-24 h-24 text-6xl"
        title={t("request_details")}
        subtitle={getRequestTypeLabel(request.type)}
        maxHeight="90vh"
        showBackButton={false}
      >
      <div className="w-full space-y-6">
        {/* Request Status Badge */}
        <div className={`p-4 rounded-xl border ${getStatusColor(request.status)}`}>
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl">{getStatusIcon(request.status)}</span>
            <span className="font-semibold text-lg">
              {t(`status_${request.status}`) || request.status}
            </span>
          </div>
          {request.status === 'pending' && (
            <p className="text-sm text-center text-gray-600 mt-2">
              {t("request_pending_message")}
            </p>
          )}
          {request.status === 'draft' && (
            <p className="text-sm text-center text-gray-600 mt-2">
              {t("request_draft_message")}
            </p>
          )}
          {request.status === 'preliminary_approved' && (
            <p className="text-sm text-center text-gray-600 mt-2">
              {t("request_preliminary_approved_message")}
            </p>
          )}
        </div>

        {/* Print QR Door Sign — approved only. A sign printed for a draft or
            rejected request would advertise an unapproved clinic. */}
        {canPrintQR && (
          <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50">
            <p className={`text-sm text-gray-600 mb-3 leading-relaxed ${isRTL ? "text-right" : "text-left"}`}>
              {t("print_qr_door_sign")}
            </p>
            <button
              onClick={handlePrintQRDoorSign}
              className="w-full min-h-[52px] px-6 py-3 rounded-xl bg-[#2E5D7D] text-white text-base font-semibold shadow-md hover:bg-[#234a5f] hover:shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <span className="text-xl">🖨️</span>
              {t("print_qr_code")}
            </button>
          </div>
        )}

        {/* Inspector Information - only show during pending_inspection */}
        {request.status === 'pending_inspection' && request.inspector && (
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-teal-50 to-cyan-50">
            <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              🔍 {t("inspector_info")}
            </h3>
            <div className="space-y-1 bg-white p-4 rounded-lg">
              <InfoRow label={t("inspector_name")} value={request.inspector.full_name} />
              <InfoRow label={t("inspector_phone")} value={request.inspector.phone_number} />
            </div>
          </div>
        )}

        {/* Request Information */}
        <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
            📄 {t("request_information")}
          </h3>
          <div className="space-y-1 bg-white p-4 rounded-lg">
            <InfoRow label={t("request_type")} value={getRequestTypeLabel(request.type)} />
            {request.request_number && (
              <InfoRow label={t("request_number")} value={request.request_number} />
            )}
            <InfoRow
              label={t("submitted_on")}
              value={request.created_at ? new Date(request.created_at).toLocaleDateString() : null}
            />
            <InfoRow
              label={t("last_updated")}
              value={request.updated_at ? new Date(request.updated_at).toLocaleDateString() : null}
            />
          </div>
        </div>

        {/* Clinic Information */}
        {request.name && (
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-green-50 to-emerald-50">
            <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              🏥 {t("clinic_information")}
            </h3>
            <div className="space-y-1 bg-white p-4 rounded-lg">
              <InfoRow label={t("clinic_name")} value={request.name} />
              <InfoRow label={t("work_hours")} value={translateValue(request.clinic_work_hours)} />
              <InfoRow
                label={t("supervises_medical_center")}
                value={request.does_supervise_medical_center ? t("yes") : t("no")}
              />
            </div>
          </div>
        )}

        {/* Location Information */}
        {request.governorate && (
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-purple-50 to-pink-50">
            <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              📍 {t("location_information")}
            </h3>
            <div className="space-y-1 bg-white p-4 rounded-lg">
              <InfoRow label={t("governorate")} value={translateValue(request.governorate)} />
              <InfoRow label={t("city")} value={isRTL ? request.area?.name_ar : request.area?.name} />
              <InfoRow label={t("neighborhood")} value={request.neighborhood} />
              <InfoRow label={t("street")} value={request.street} />
              <InfoRow label={t("house_number")} value={request.house_number} />
              <InfoRow label={t("nearest_landmark")} value={request.nearest_landmark} />
            </div>
          </div>
        )}

        {/* Additional Notes */}
        {request.applicant_notes && (
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-yellow-50 to-orange-50">
            <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              📝 {t("additional_notes")}
            </h3>
            <div className="space-y-1 bg-white p-4 rounded-lg">
              <p className="text-sm text-gray-900">{request.applicant_notes}</p>
            </div>
          </div>
        )}

        {/* Documents/Attachments */}
        {request.documents && Object.keys(request.documents).length > 0 && (
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-indigo-50 to-purple-50">
            <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              📎 {t("documents")}
            </h3>
            <div className="space-y-4 bg-white p-4 rounded-lg">
              {Object.entries(request.documents)
                .sort(([keyA], [keyB]) => {
                  // Sort order: signboards first, then rooms, then others
                  const getOrder = (k: string) => {
                    if (k.includes('signboard')) return 1;
                    if (k.includes('rooms')) return 2;
                    return 3;
                  };
                  return getOrder(keyA) - getOrder(keyB);
                })
                .map(([key, value]: [string, any]) => {
                if (!value) return null;

                // Format the key to a readable/translated name
                const formatKey = (k: string) => {
                  // Try translation first
                  const translationKey = k;
                  const translated = t(translationKey);
                  if (translated !== translationKey) {
                    return translated;
                  }

                  // Try with _title suffix
                  const titleKey = `${k}_title`;
                  const titleTranslated = t(titleKey);
                  if (titleTranslated !== titleKey) {
                    return titleTranslated;
                  }

                  // Fallback to formatted string
                  return k
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                };

                // Handle array of documents (e.g., clinic_rooms with multiple images)
                if (Array.isArray(value)) {
                  return (
                    <div key={key} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xl">📁</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatKey(key)} ({value.length} {value.length === 1 ? t("file") : t("files")})
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {value.map((url: string, index: number) => (
                          <div
                            key={index}
                            onClick={() => setSelectedImage(url)}
                            className="relative cursor-pointer group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#2E5D7D] transition-all"
                          >
                            <img
                              src={url}
                              alt={`${formatKey(key)} ${index + 1}`}
                              className="w-full h-40 object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                              <span className="text-white text-2xl opacity-0 group-hover:opacity-100 transition-all">🔍</span>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                              <span className="text-white text-xs font-medium">
                                {formatKey(key)} {index + 1}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Handle single document (e.g., clinic_signboard_outer)
                return (
                  <div key={key} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">📄</span>
                      <span className="text-sm font-semibold text-gray-900">{formatKey(key)}</span>
                    </div>
                    <div
                      onClick={() => setSelectedImage(value)}
                      className="relative cursor-pointer group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#2E5D7D] transition-all"
                    >
                      <img
                        src={value}
                        alt={formatKey(key)}
                        className="w-full h-48 object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                        <span className="text-white text-3xl opacity-0 group-hover:opacity-100 transition-all">🔍</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Review Notes (if rejected) */}
        {request.status === 'rejected' && request.review_notes && (
          <div className="p-5 rounded-xl border-2 border-red-300 bg-gradient-to-br from-red-50 to-orange-50">
            <h3 className={`text-lg font-bold mb-4 text-red-800 ${isRTL ? "text-right" : "text-left"}`}>
              ❌ {t("rejection_reason")}
            </h3>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-gray-900">{request.review_notes}</p>
            </div>
          </div>
        )}
      </div>
      </ResponsiveFormLayout>

      {/* Image Modal/Lightbox - Full Page */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center p-4"
          style={{ zIndex: 9999999 }}
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button - Top Left to avoid logout button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 left-4 text-white text-5xl hover:text-gray-300 transition-all z-10 flex items-center gap-2"
            >
              <span className="text-4xl">←</span>
              <span className="text-lg font-semibold">{t("close")}</span>
            </button>

            {/* Image */}
            <img
              id="lightbox-image"
              src={selectedImage}
              alt={t("document_preview")}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

          </div>
        </div>
      )}

      {/* Hidden QR Door Sign Print View */}
      {canPrintQR && clinicId && (
        <QRDoorSignPrint
          clinicId={clinicId}
          clinicName={request?.name}
          doctorName={language === "ar" ? doctorInfo?.full_name_ar : doctorInfo?.full_name}
        />
      )}
    </>
  );
};

export default RequestDetailScreen;
